import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { ArrowLeft, Search, Upload, Trash2, FileText, Eye, Download } from 'lucide-react';
import { toast } from 'sonner';
import { useAutoRefresh } from '@/hooks/use-auto-refresh';
import { usePermissions } from '@/hooks/use-permissions';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function LnxReports({ user, onLogout }) {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions(user);
  const [reports, setReports] = useState([]);
  const [filteredReports, setFilteredReports] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const [uploadingReportId, setUploadingReportId] = useState(null);
  const [viewPdfData, setViewPdfData] = useState(null);
  const [showPdfViewer, setShowPdfViewer] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [reportToDeletePdf, setReportToDeletePdf] = useState(null);
  const [statusFilter, setStatusFilter] = useState('ALL');

  useEffect(() => {
    fetchLnxReports();
  }, []);

  useEffect(() => {
    filterReports();
  }, [searchQuery, reports, statusFilter]);

  const fetchLnxReports = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/lnx-reports`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReports(response.data);
      setFilteredReports(response.data);
    } catch (error) {
      console.error('Failed to fetch LN-X reports:', error);
      if (error.response?.status === 403) {
        toast.error('Access denied. You don\'t have permission to view LN-X Reports.');
      } else {
        toast.error('Failed to load LN-X reports');
      }
    }
  };

  // Auto-refresh every 5 seconds, but pause when viewing or uploading
  const isUserEditing = uploadingPdf || deleteDialogOpen || showPdfViewer;
  useAutoRefresh(fetchLnxReports, 5, isUserEditing);

  const filterReports = () => {
    let filtered = reports;

    // Filter by search query
    if (searchQuery.trim()) {
      filtered = filtered.filter(report =>
        report.design_code?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        report.design_name?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Filter by status
    if (statusFilter !== 'ALL') {
      filtered = filtered.filter(report => report.status === statusFilter);
    }

    setFilteredReports(filtered);
  };

  const handleFileSelect = (reportId) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.pdf';
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (file) {
        if (!file.type.includes('pdf')) {
          toast.error('Please select a PDF file');
          return;
        }
        setSelectedFile(file);
        setUploadingReportId(reportId);
        handleUploadPdf(reportId, file);
      }
    };
    input.click();
  };

  const handleUploadPdf = async (reportId, file) => {
    setUploadingPdf(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);

      await axios.post(`${API}/lnx-reports/${reportId}/upload-pdf`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      toast.success('LN-X Report PDF uploaded successfully');
      setSelectedFile(null);
      setUploadingReportId(null);
      fetchLnxReports();
    } catch (error) {
      console.error('Failed to upload PDF:', error);
      toast.error('Failed to upload PDF');
    } finally {
      setUploadingPdf(false);
    }
  };

  const handleViewPdf = async (report) => {
    if (!report.pdf_url) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}${report.pdf_url}`);
      const blob = await response.blob();
      const reader = new FileReader();
      reader.onloadend = () => {
        setViewPdfData({
          url: reader.result,
          title: `LN-X Report - ${report.design_code}`,
        });
        setShowPdfViewer(true);
      };
      reader.readAsDataURL(blob);
    } catch (error) {
      console.error('Failed to load PDF:', error);
      toast.error('Failed to load PDF');
    }
  };

  const handleDownloadPdf = (report) => {
    if (report.pdf_url) {
      const link = document.createElement('a');
      link.href = `${BACKEND_URL}${report.pdf_url}`;
      link.download = `lnx_report_${report.design_code}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleDeletePdfClick = (report) => {
    setReportToDeletePdf(report);
    setDeleteDialogOpen(true);
  };

  const confirmDeletePdf = async () => {
    if (!reportToDeletePdf) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/lnx-reports/${reportToDeletePdf.id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      toast.success('LN-X Report PDF deleted successfully');
      setDeleteDialogOpen(false);
      setReportToDeletePdf(null);
      fetchLnxReports();
    } catch (error) {
      console.error('Failed to delete PDF:', error);
      toast.error('Failed to delete PDF');
    }
  };

  const getStatusBadge = (status) => {
    if (status === 'Completed') {
      return 'bg-green-100 text-green-800';
    }
    return 'bg-yellow-100 text-yellow-800';
  };

  // Check permission
  if (!hasPermission('lnx_reports_view')) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-2">Access Denied</h2>
          <p className="text-gray-600 mb-4">You don't have permission to access LN-X Reports.</p>
          <Button onClick={() => navigate('/design-studio')}>Go to Design Studio</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b-2 border-black">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/design-studio')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-3xl font-bold tracking-tight uppercase" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                LN-X Reports
              </h1>
              <p className="text-sm text-gray-600 mt-1">Manage LN-X Reports for Finished Good Designs</p>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-6 py-8">
        {/* Search and Filters */}
        <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Enter LN-X code or scan barcode"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <SearchableSelect
            options={[
              { value: 'ALL', label: 'All Status' },
              { value: 'Pending', label: 'Pending' },
              { value: 'Completed', label: 'Completed' }
            ]}
            value={statusFilter}
            onChange={setStatusFilter}
            placeholder="Filter by status"
          />
        </div>

        {/* Reports Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredReports.map((report) => (
            <div key={report.id} className="bg-white border-2 border-black p-4 hover:shadow-lg transition-shadow">
              <div className="flex justify-between items-start mb-3">
                <div className="flex-1">
                  <h3 className="font-bold text-lg">{report.design_code}</h3>
                  <p className="text-sm text-gray-600">{report.design_name}</p>
                </div>
                <span className={`px-2 py-1 text-xs font-bold rounded ${getStatusBadge(report.status)}`}>
                  {report.status}
                </span>
              </div>

              <div className="flex gap-2 mt-4">
                {!report.pdf_url ? (
                  <Button
                    onClick={() => handleFileSelect(report.id)}
                    size="sm"
                    className="flex-1 bg-black hover:bg-gray-800"
                    disabled={uploadingPdf && uploadingReportId === report.id}
                  >
                    <Upload className="w-4 h-4 mr-1" />
                    {uploadingPdf && uploadingReportId === report.id ? 'Uploading...' : 'Upload PDF'}
                  </Button>
                ) : (
                  <>
                    <Button
                      onClick={() => handleViewPdf(report)}
                      size="sm"
                      variant="outline"
                      className="flex-1"
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      View
                    </Button>
                    <Button
                      onClick={() => handleDownloadPdf(report)}
                      size="sm"
                      variant="outline"
                    >
                      <Download className="w-4 h-4" />
                    </Button>
                    <Button
                      onClick={() => handleDeletePdfClick(report)}
                      size="sm"
                      variant="outline"
                      className="text-red-600 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>

        {filteredReports.length === 0 && (
          <div className="text-center py-12">
            <FileText className="w-16 h-16 mx-auto text-gray-400 mb-4" />
            <p className="text-gray-600">No LN-X reports found</p>
            <p className="text-sm text-gray-500 mt-2">
              LN-X reports are automatically created when designs reach "Finished Good" status
            </p>
          </div>
        )}
      </main>

      {/* PDF Viewer Dialog */}
      {showPdfViewer && viewPdfData && (
        <Dialog open={showPdfViewer} onOpenChange={(open) => {
          setShowPdfViewer(open);
          if (!open) {
            setViewPdfData(null);
          }
        }}>
          <DialogContent className="max-w-screen max-h-screen w-screen h-screen p-0 m-0 rounded-none border-0">
            <DialogHeader className="absolute top-0 left-0 right-0 z-10 px-6 py-3 bg-white border-b">
              <div className="flex items-center justify-between">
                <DialogTitle>{viewPdfData.title}</DialogTitle>
                <button 
                  onClick={() => setShowPdfViewer(false)}
                  className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
                >
                  ×
                </button>
              </div>
            </DialogHeader>
            {viewPdfData && (
              <iframe
                src={viewPdfData.url}
                className="w-full h-full border-0"
                title="LN-X Report PDF"
                style={{ marginTop: '60px' }}
                type="application/pdf"
              />
            )}
          </DialogContent>
        </Dialog>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete LN-X Report PDF</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600 mb-4">
            Are you sure you want to delete the PDF for design {reportToDeletePdf?.design_code}? This will change the status back to Pending.
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={confirmDeletePdf}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
